import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Product } from '../services/productService';
import { useAuth } from './AuthContext';

export interface CartItem {
    product: Product;
    quantity: number;
    unitPrice: number;
    total: number;
    providerCost?: number; // Added for wholesale calculations
}

interface CartContextType {
    cart: CartItem[];
    addToCart: (product: Product) => void;
    removeFromCart: (productId: number) => void;
    updateQuantity: (productId: number, quantity: number) => void;
    clearCart: () => void;
    totalAmount: number;
    // Customer Persistence
    customer: { name: string; phone: string; email: string; type?: string };
    setCustomer: (data: { name: string; phone: string; email: string; type?: string }) => void;
    
    // Wholesale mode
    isWholesaleMode: boolean;
    setIsWholesaleMode: (mode: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();

    const [cart, setCart] = useState<CartItem[]>(() => {
        // Only load cart if it belongs to the current user (checked later) or load anyway and clear if mismatch?
        // Better: Load, then useEffect checks ownership.
        try {
            const storedCart = localStorage.getItem('erp_cart');
            return storedCart ? JSON.parse(storedCart) : [];
        } catch (error) {
            console.error('Error loading cart from localStorage', error);
            return [];
        }
    });

    // Customer State
    const [customer, setCustomerState] = useState<{ name: string; phone: string; email: string; type?: string }>(() => {
        try {
            const storedCustomer = localStorage.getItem('erp_cart_customer');
            return storedCustomer ? JSON.parse(storedCustomer) : { name: '', phone: '', email: '' }; // Added missing fallback
        } catch (error) {
            return { name: '', phone: '', email: '' }; // Added missing fallback
        }
    });

    // Wholesale State
    const [isWholesaleMode, setIsWholesaleMode] = useState<boolean>(() => {
        try {
            return localStorage.getItem('erp_cart_wholesale') === 'true';
        } catch (error) {
            return false;
        }
    });

    // Session Cleanup Logic
    useEffect(() => {
        if (user) {
            const storedOwner = localStorage.getItem('erp_cart_owner');
            const currentUserId = user.uid || user.id_usuario?.toString();

            if (storedOwner && storedOwner !== currentUserId) {
                // User changed! Clear cart.
                setCart([]);
                setCustomerState({ name: '', phone: '', email: '' });
                setIsWholesaleMode(false);
                localStorage.removeItem('erp_cart');
                localStorage.removeItem('erp_cart_customer');
                localStorage.removeItem('erp_cart_wholesale');
            }

            // Update owner
            if (currentUserId) {
                localStorage.setItem('erp_cart_owner', currentUserId);
            }
        } else {
            // Optional: Clear cart on logout? The requirement says "reinicio solo si cambiamos de usuario".
            // If I logout, I am no user. If I login as SAME user, cart should persist?
            // If I login as DIFFERENT user, cart should clear.
            // The logic above handles "Different User" (stored != current).
            // But if I am null (logged out), should I clear? 
            // "si me salgo de la sesión e inicio sesión con otra cuenta... debería de quitarse... persiste aunque cambiemos de usuario"
            // Implies the issue is preserving state BETWEEN different users.
            // If I logout, user is null. I shouldn't necessarily delete the cart yet, maybe I logged out by accident.
            // But when I log IN as new user, the check (storedOwner !== currentUserId) will fire and clear it.
            // So this logic is sufficient.
        }
    }, [user]);

    useEffect(() => {
        try {
            localStorage.setItem('erp_cart', JSON.stringify(cart));
            localStorage.setItem('erp_cart_customer', JSON.stringify(customer));
            localStorage.setItem('erp_cart_wholesale', String(isWholesaleMode));
        } catch (error) {
            console.error('Error saving cart to localStorage', error);
        }
    }, [cart, customer, isWholesaleMode]);

    const calculateWholesalePrice = (basePrice: number) => {
        return Math.round(basePrice * 0.78);
    };

    // Auto-recalculate entire cart when mode toggles
    useEffect(() => {
        setCart(prevCart => prevCart.map(item => {
            if (isWholesaleMode) {
                const basePrice = Number(item.product.precio) || 0;
                if (basePrice > 0) {
                    const wholesaleUnitPrice = calculateWholesalePrice(basePrice);
                    return {
                        ...item,
                        unitPrice: wholesaleUnitPrice,
                        total: wholesaleUnitPrice * item.quantity,
                        providerCost: undefined
                    };
                }
            } else {
                const normalPrice = Number(item.product.precio) || 0;
                return {
                    ...item,
                    unitPrice: normalPrice,
                    total: normalPrice * item.quantity
                };
            }
            return item;
        }));
    }, [isWholesaleMode]);

    const addToCart = (product: Product) => {
        setCart((prevCart) => {
            const existingItem = prevCart.find((item) => item.product.id_producto === product.id_producto);

            if (existingItem) {
                return prevCart.map((item) =>
                    item.product.id_producto === product.id_producto
                        ? {
                            ...item,
                            quantity: item.quantity + 1,
                            total: (item.quantity + 1) * item.unitPrice
                        }
                        : item
                );
            }

            const newQuantity = 1;
            let price = Number(product.precio) || 0;

            if (isWholesaleMode && price > 0) {
                price = calculateWholesalePrice(price);
            }

            return [
                ...prevCart,
                {
                    product,
                    quantity: newQuantity,
                    unitPrice: price,
                    total: newQuantity * price,
                    providerCost: undefined
                }
            ];
        });
    };

    const removeFromCart = (productId: number) => {
        setCart((prevCart) => prevCart.filter((item) => item.product.id_producto !== productId));
    };

    const updateQuantity = (productId: number, quantity: number) => {
        if (quantity < 1) return;

        setCart((prevCart) =>
            prevCart.map((item) => {
                if (item.product.id_producto === productId) {
                    return {
                        ...item,
                        quantity,
                        total: quantity * item.unitPrice
                    };
                }
                return item;
            })
        );
    };



    const clearCart = () => {
        setCart([]);
        setCustomerState({ name: '', phone: '', email: '' });
        setIsWholesaleMode(false);
        localStorage.removeItem('erp_cart');
        localStorage.removeItem('erp_cart_customer');
        localStorage.removeItem('erp_cart_wholesale');
    };

    const setCustomer = (data: { name: string; phone: string; email: string; type?: string }) => {
        setCustomerState(data);
    };

    const totalAmount = cart.reduce((sum, item) => sum + item.total, 0);

    return (
        <CartContext.Provider value={{
            cart, addToCart, removeFromCart, updateQuantity, clearCart, totalAmount,
            customer, setCustomer,
            isWholesaleMode, setIsWholesaleMode
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
